import React, { useMemo, useState } from 'react';
import * as d3 from 'd3';
import { useDimensionsRef } from 'hooks';
import { useChartTooltip } from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/useChartTooltip';
import { useFulfillmentDashboardStore } from 'pages/ReportsPage/components/AnalyticsDashboards/FulfillmentDashboard/store/fulfillmentDashboard.store';
import { useChartColors } from 'pages/ReportsPage/components/AnalyticsDashboards/chartColors';
import { formatDashboardNumber } from 'pages/ReportsPage/components/AnalyticsDashboards/formatDashboardNumber';
import { EmptyStateContainer } from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/OverviewDashboard.styled';
import {
  ChartLegend,
  ChartLegendItem,
  ChartLegendDot,
  XAxisTickLabel,
} from 'pages/ReportsPage/components/AnalyticsDashboards/FulfillmentDashboard/FulfillmentDashboard.styled';

const MARGIN = { top: 20, right: 20, bottom: 200, left: 50 };
const CHART_HEIGHT = 490;

const X_LABEL_WIDTH = 160;
const X_LABEL_HEIGHT = 90;
const GROUP_PADDING = 0.35;
const MAX_GROUP_WIDTH = 120;
const BAR_GAP_RATIO = 0.15;
const BAR_RX = 3;
const Y_DOMAIN_STEP = 20;

const SERIES = ['rate', 'time'] as const;
type SeriesKey = typeof SERIES[number];

export const CalloutFulfillmentChart: React.FC = () => {
  const data = useFulfillmentDashboardStore((s) => s.fulfillmentByPosition);
  const palette = useChartColors();
  const [containerRef, { width: containerWidth }] = useDimensionsRef<HTMLDivElement>();
  const tooltip = useChartTooltip();
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  const rateColor = palette.chartColors[1] || '#4CAF50';
  const timeColor = '#5B3FC5';

  const innerWidth = Math.max(containerWidth - MARGIN.left - MARGIN.right, 0);
  const innerHeight = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;

  const chartData = data;

  const xScale = useMemo(() => {
    if (!chartData) return null;
    return d3
      .scaleBand<string>()
      .domain(chartData.map((d) => d.position))
      .range([0, innerWidth])
      .padding(GROUP_PADDING);
  }, [chartData, innerWidth]);

  const groupWidth = xScale ? Math.min(xScale.bandwidth(), MAX_GROUP_WIDTH) : 0;

  const seriesScale = useMemo(
    () =>
      d3.scaleBand<SeriesKey>().domain(SERIES).range([0, groupWidth]).paddingInner(BAR_GAP_RATIO),
    [groupWidth],
  );

  const yDomainMax = useMemo(() => {
    const maxLag =
      (chartData && d3.max(chartData, (d) => d.avgCalloutFulfillmentLagHours ?? 0)) || 0;
    return Math.max(100, Math.ceil(maxLag / Y_DOMAIN_STEP) * Y_DOMAIN_STEP);
  }, [chartData]);

  const yScale = useMemo(
    () => d3.scaleLinear().domain([0, yDomainMax]).range([innerHeight, 0]),
    [yDomainMax, innerHeight],
  );

  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((step) => yDomainMax * step);

  if (!chartData || chartData.length === 0) {
    return (
      <div ref={containerRef} style={{ width: '100%' }}>
        <EmptyStateContainer>No data available</EmptyStateContainer>
      </div>
    );
  }

  if (!containerWidth || !xScale) {
    return <div ref={containerRef} style={{ width: '100%', minHeight: 200 }} />;
  }

  return (
    <div ref={containerRef} style={{ width: '100%' }}>
      <ChartLegend>
        <ChartLegendItem>
          <ChartLegendDot $color={rateColor} />
          <span>Fulfillment Rate</span>
        </ChartLegendItem>
        <ChartLegendItem>
          <ChartLegendDot $color={timeColor} />
          <span>Avg Fulfillment Time (Hrs)</span>
        </ChartLegendItem>
      </ChartLegend>
      <svg
        width={containerWidth}
        height={CHART_HEIGHT}
        aria-label="Fulfillment rate for call outs bar chart"
      >
        <g transform={`translate(${MARGIN.left},${MARGIN.top})`}>
          {yTicks.map((tick) => (
            <g key={tick}>
              <line
                x1={0}
                x2={innerWidth}
                y1={yScale(tick)}
                y2={yScale(tick)}
                stroke="#E8E8E8"
                strokeDasharray={tick === 0 ? 'none' : '4,4'}
              />
              <text
                x={-8}
                y={yScale(tick)}
                textAnchor="end"
                dominantBaseline="middle"
                fontSize="12px"
                fill="#999"
              >
                {tick}
              </text>
            </g>
          ))}
          {chartData.map((d, i) => {
            const bandX = xScale(d.position) || 0;
            const groupX = bandX + (xScale.bandwidth() - groupWidth) / 2;
            const labelX = bandX + xScale.bandwidth() / 2;
            const labelY = innerHeight + 12;
            const rate = d.fulfillmentPercentage;
            const lag = d.avgCalloutFulfillmentLagHours ?? 0;
            const rateText = formatDashboardNumber(rate);
            const lagText = formatDashboardNumber(lag);
            const tooltipHtml = `<strong>${d.position}</strong><br/>Fulfillment Rate: ${rateText}%<br/>Avg Fill Time: ${lagText}h`;
            const ariaLabel = `${d.position}: ${rateText}% rate, ${lagText}h avg`;

            const renderBar = (series: SeriesKey, value: number, fill: string) => {
              if (value <= 0) return null;
              const barY = yScale(value);
              return (
                <rect
                  x={groupX + (seriesScale(series) || 0)}
                  y={barY}
                  width={seriesScale.bandwidth()}
                  height={innerHeight - barY}
                  fill={fill}
                  rx={BAR_RX}
                />
              );
            };

            return (
              <g
                key={d.position}
                role="img"
                aria-label={ariaLabel}
                tabIndex={0}
                style={{ cursor: 'pointer' }}
                onMouseEnter={(e) => {
                  setHoveredIdx(i);
                  tooltip.show(e, tooltipHtml);
                }}
                onMouseMove={(e) => tooltip.move(e)}
                onMouseLeave={() => {
                  setHoveredIdx(null);
                  tooltip.hide();
                }}
                onFocus={(e) => {
                  setHoveredIdx(i);
                  tooltip.show(e, tooltipHtml);
                }}
                onBlur={() => {
                  setHoveredIdx(null);
                  tooltip.hide();
                }}
              >
                <foreignObject
                  x={labelX - X_LABEL_WIDTH}
                  y={labelY}
                  width={X_LABEL_WIDTH}
                  height={X_LABEL_HEIGHT}
                  transform={`rotate(-45, ${labelX}, ${labelY})`}
                  style={{ overflow: 'visible' }}
                >
                  <XAxisTickLabel title={d.position}>{d.position}</XAxisTickLabel>
                </foreignObject>
                <g opacity={hoveredIdx === i ? 0.8 : 1}>
                  {renderBar('rate', rate, rateColor)}
                  {renderBar('time', lag, timeColor)}
                </g>
              </g>
            );
          })}
        </g>
      </svg>
    </div>
  );
};
